import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useLogin() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const { send, isConnected, isOpen, connect, registerHandler } = useWebSocketContext();
  const mountedRef = useRef(true);
  const connectionAttemptedRef = useRef(false);

  const login = useCallback(async ({ email, password }) => {
    if (!mountedRef.current) return;

    setIsLoading(true);
    setError('');

    try {
      // Ensure WebSocket is connected
      if (!isConnected) {
        if (!connectionAttemptedRef.current) {
          connectionAttemptedRef.current = true;
          await connect();
        }

        await new Promise((resolve, reject) => {
          const start = Date.now();
          const interval = setInterval(() => {
            if (!mountedRef.current) {
              clearInterval(interval);
              reject(new Error('Component unmounted'));
            } else if (isOpen()) {
              clearInterval(interval);
              resolve(true);
            } else if (Date.now() - start > 10000) {
              clearInterval(interval);
              reject(new Error('Connection timeout'));
            }
          }, 100);
        });
      }

      // Send login request
      const response = await send('cms_user_login', { identifier: email, password });
      console.log('Login response:', response);

      if (!response || response.type !== 'cms_user_login_response') {
        throw new Error('Invalid response from server');
      }

      if (!response.success) {
        throw new Error(response.message || 'Login failed');
      }

      // Extract user info from response
      const userData = {
        admin_id: response.admin_id,
        group_id: response.group_id,
        group_name: response.group_name,
        role_id: response.role_id,
      };

      // Save state & localStorage
      setUser(userData);
      setToken(response.token);
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('group_id', response.group_id);
      localStorage.setItem('role_id', response.role_id);
      localStorage.setItem('group_name', response.group_name);

      return { success: true, user: userData, token: response.token };
    } catch (err) {
      setError(err.message);
      setUser(null);
      setToken(null);
      return { success: false, error: err.message };
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [isConnected, isOpen, connect, send]);

  useEffect(() => {
    const unregister = registerHandler('user_update', (msg) => {
      if (mountedRef.current) setUser(msg.data);
    });
    return () => unregister();
  }, [registerHandler]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false };
  }, []);

  return { user, token, isLoading, error, login };
}
